import { ApiService } from '../ApiService';
import type {
  ApiBalanceSheetResponse,
  ApiIncomeStatementResponse,
  ApiCashFlowResponse,
  ApiTrialBalanceResponse,
  EquityChangesData,
  NotesData,
  FinancialStatementType,
  JournalBookData,
} from '../../../types/reports';
import type { AgedBalanceResponse } from '../../../types/aged-balance';
import { CurrencyCode } from '../../../config/currency';

export const reportsApi = {
  /**
   * Bilan SYSCOHADA (GET /reports/balance-sheet)
   * `date` et `fiscalYear` sont REQUIS par le backend.
   */
  getBalanceSheet: (params: {
    date:        string;
    fiscalYear:  string;
    comparative?: boolean;
    currency?:   CurrencyCode;
    companyId?:  string;
  }) =>
    ApiService.get<{ success: true; data: ApiBalanceSheetResponse }>('/reports/balance-sheet', params),

  /**
   * Compte de résultat SYSCOHADA (GET /reports/income-statement)
   * `startDate`, `endDate` et `fiscalYear` sont REQUIS.
   */
  getIncomeStatement: (params: {
    startDate:    string;
    endDate:      string;
    fiscalYear?:  string;   // optionnel — non requis par le backend
    comparative?: boolean;
    currency?:    CurrencyCode;
    companyId?:   string;
  }) =>
    ApiService.get<{ success: true; data: ApiIncomeStatementResponse }>('/reports/income-statement', params),

  /**
   * Flux de trésorerie SYSCOHADA (GET /reports/cash-flow)
   * `startDate`, `endDate` et `fiscalYear` sont REQUIS.
   */
  getCashFlowStatement: (params: {
    startDate:    string;
    endDate:      string;
    fiscalYear?:  string;   // optionnel — non requis par le backend
    comparative?: boolean;
    currency?:    CurrencyCode;
    companyId?:   string;
  }) =>
    ApiService.get<{ success: true; data: ApiCashFlowResponse }>('/reports/cash-flow', params),

  /**
   * Balance générale 6 colonnes SYSCOHADA (GET /reports/trial-balance)
   * `date` et `fiscalYear` sont REQUIS.
   * `openingDate` requis pour le format 6 colonnes.
   */
  getTrialBalance: (params: {
    date:                   string;
    fiscalYear:             string;
    includeOpeningBalance?: boolean;
    openingDate?:           string;
    groupByClass?:          boolean;
    companyId?:             string;
  }) =>
    ApiService.get<{ success: true; data: ApiTrialBalanceResponse }>('/reports/trial-balance', params),

  /**
   * Variation des capitaux propres (GET /reports/equity-changes)
   */
  getEquityChanges: (params: {
    startDate:  string;
    endDate:    string;
    currency?:  CurrencyCode;
    fiscalYear?: string;
    companyId?: string;
  }) =>
    ApiService.get<{ success: true; data: EquityChangesData }>('/reports/equity-changes', params),

  /**
   * Notes aux états financiers (GET /reports/notes)
   */
  getNotes: (params: {
    date:       string;
    type:       'balance' | 'income' | 'all';
    fiscalYear?: string;
    companyId?: string;
  }) =>
    ApiService.get<{ success: true; data: NotesData }>('/reports/notes', params),

  /**
   * Générer un rapport financier (POST /reports/generate)
   * C'est l'endpoint principal pour générer tous types de rapports
   * 
   * Selon la documentation API :
   * - type (required): Type de rapport (balance, income, cashflow, etc.)
   * - format (optional): json, pdf, excel (défaut: json)
   * - date (optional): Date de fin pour le bilan
   * - startDate (optional): Date de début pour compte de résultat/flux de trésorerie
   * - endDate (optional): Date de fin pour compte de résultat/flux de trésorerie
   * - comparative (optional): Inclure données comparatives
   * - currency (optional): Code devise
   * - includeNotes (optional): Inclure les notes annexes
   * - filters (optional): Filtres additionnels
   */
  /**
   * Génération générique (POST /reports/generate)
   * Utiliser les endpoints GET dédiés pour balance/income/cashflow/trial-balance.
   * Cet endpoint couvre les types restants (analytical, social, statistics, etc.)
   */
  generateReport: (params: {
    type:         FinancialStatementType;
    format?:      'json' | 'pdf' | 'excel' | 'csv' | 'html';
    /** UUID exercice fiscal */
    fiscalYear?:  string;
    date?:        string;
    startDate?:   string;
    endDate?:     string;
    comparative?: boolean;
    currency?:    CurrencyCode;
    includeNotes?: boolean;
    filters?: {
      analytical_account_id?: string;
      cost_center_id?:        string;
      department_id?:         string;
      project_id?:            string;
      business_unit_id?:      string;
    };
  }) =>
    ApiService.post<{ success: true; reportType: string; generatedAt: string; data: any }>(
      '/reports/generate',
      params
    ),

  /**
   * Export d’un rapport généré (POST /reports/export)
   * Retourne un `downloadUrl` + `expiresAt`.
   */
  exportReport: (params: {
    type:       FinancialStatementType;
    format:     'pdf' | 'excel' | 'csv' | 'html';
    data:       any;
    title?:     string;
    organization?: {
      name:               string;
      address?:           string;
      registrationNumber?: string;
      taxId?:             string;
    };
    generatedBy?: string;
    isAudited?:   boolean;
    currency?:    string;
  }) =>
    ApiService.post<{ success: true; downloadUrl: string; expiresAt: string }>('/reports/export', params),

  // Nouveaux endpoints pour recherche et filtres avancés
  searchReports: (params: {
    query: string;
    type?: FinancialStatementType;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) =>
    ApiService.get<any>('/reports/search', params),

  getReportTemplates: () =>
    ApiService.get<any[]>('/reports/templates'),

  saveReportTemplate: (template: {
    name: string;
    type: FinancialStatementType;
    configuration: any;
  }) =>
    ApiService.post<any>('/reports/templates', template),

  // ============================================
  // NOUVEAUX RAPPORTS FINANCIERS
  // ============================================

  /**
   * Livre journal — toutes les écritures validées en ordre chronologique
   * @see GET /financial-statements/journal-book
   */
  getJournalBook: (params: {
    fiscal_year: string;
    start_date?: string;
    end_date?: string;
  }) =>
    ApiService.get<{ success: true; data: JournalBookData }>('/financial-statements/journal-book', params),

  /**
   * Balance âgée clients — créances non lettrées (comptes 41x)
   * @see GET /financial-statements/aged-receivables
   */
  getAgedReceivables: (params: {
    fiscal_year: string;
    as_of_date?: string;
  }) =>
    ApiService.get<{ success: true; data: AgedBalanceResponse }>('/financial-statements/aged-receivables', params),

  /**
   * Balance âgée fournisseurs — dettes non lettrées (comptes 40x)
   * @see GET /financial-statements/aged-payables
   */
  getAgedPayables: (params: {
    fiscal_year: string;
    as_of_date?: string;
  }) =>
    ApiService.get<{ success: true; data: AgedBalanceResponse }>('/financial-statements/aged-payables', params),

  /**
   * Grand livre — écritures postées groupées par compte
   * @see GET /financial-statements/general-ledger
   */
  getGeneralLedger: (params: {
    fiscal_year: string;
    start_date?: string;
    end_date?: string;
  }) =>
    ApiService.get<{ success: true; data: any }>('/financial-statements/general-ledger', params),
};